<script setup>
import { useI18n } from 'vue-i18n'
import { SUPPORTED_LOCALES, loadLocale } from '@/i18n'

/**
 * Segmented EN | ID control. Rendered in the header on every breakpoint —
 * on phones it sits beside the burger, so the toggle never disappears with
 * the collapsed menu.
 */
defineProps({
  tone: {
    type: String,
    default: 'light',
    validator: (value) => ['light', 'dark'].includes(value)
  }
})

const { locale, t } = useI18n()

/** Fetch the messages first, then flip — no flash of the fallback language. */
const select = async (code) => {
  if (locale.value === code) return
  await loadLocale(code)
  locale.value = code
}
</script>

<template>
  <div
    class="flex items-center rounded-[10px] border p-0.5"
    :class="tone === 'dark' ? 'border-white/15 bg-white/5' : 'border-hairline bg-white'"
    role="group"
    :aria-label="t('ui.language.label')"
  >
    <button
      v-for="option in SUPPORTED_LOCALES"
      :key="option.code"
      type="button"
      class="grid min-h-[40px] min-w-[44px] place-items-center rounded-lg px-2.5 font-display text-[13px] font-bold tracking-[0.06em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-deep"
      :class="
        locale === option.code
          ? 'bg-ink text-white'
          : tone === 'dark'
            ? 'text-night-muted hover:text-night-text'
            : 'text-muted hover:text-ink'
      "
      :aria-pressed="locale === option.code"
      :aria-label="t('ui.language.switchTo', { language: t(option.nameKey) })"
      @click="select(option.code)"
    >
      {{ option.short }}
    </button>
  </div>
</template>
